// src/components/health/dashboard/HealthAlerts.tsx
// Health Alerts and Reminders Component

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  AlertTriangle, 
  Info, 
  CheckCircle2, 
  Bell,
  X,
  ChevronRight,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import type { HealthAlert } from '@/features/health/types/healthTypes/reportTypes';

// ============================================================================
// Types
// ============================================================================

interface HealthAlertsProps {
  alerts: HealthAlert[];
  onDismiss?: (alertId: string) => void;
  maxVisible?: number;
}

// ============================================================================
// Component
// ============================================================================

export const HealthAlerts: React.FC<HealthAlertsProps> = ({
  alerts,
  onDismiss,
  maxVisible = 3,
}) => {
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());

  const visibleAlerts = alerts
    .filter(alert => !alert.dismissed && !dismissedIds.has(alert.id))
    .slice(0, maxVisible);

  const handleDismiss = (alertId: string) => {
    setDismissedIds(prev => new Set([...prev, alertId]));
    onDismiss?.(alertId);
  };

  if (visibleAlerts.length === 0) {
    return null;
  }

  return (
    <div className="space-y-3">
      <AnimatePresence mode="popLayout">
        {visibleAlerts.map((alert, index) => (
          <AlertCard
            key={alert.id}
            alert={alert}
            onDismiss={() => handleDismiss(alert.id)}
            index={index}
          />
        ))}
      </AnimatePresence>

      {alerts.filter(a => !a.dismissed && !dismissedIds.has(a.id)).length > maxVisible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center"
        >
          <button className="text-sm text-sage-dark hover:text-sage font-medium">
            View all {alerts.filter(a => !a.dismissed).length} alerts
          </button>
        </motion.div>
      )}
    </div>
  );
};

// ============================================================================
// Alert Card Component
// ============================================================================

interface AlertCardProps {
  alert: HealthAlert;
  onDismiss: () => void;
  index: number;
}

const AlertCard: React.FC<AlertCardProps> = ({ alert, onDismiss, index }) => {
  const config = getAlertConfig(alert.type);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: -10, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95, transition: { duration: 0.2 } }}
      transition={{ delay: index * 0.05, duration: 0.3 }}
      className={`
        relative flex items-start gap-4 p-4 rounded-xl border
        ${config.bgClass} ${config.borderClass}
      `}
    >
      {/* Icon */}
      <div className={`
        flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center
        ${config.iconBgClass}
      `}>
        <config.icon className={`w-5 h-5 ${config.iconClass}`} />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <h4 className={`font-medium ${config.titleClass}`}>
          {alert.title}
        </h4>
        <p className="text-sm text-warm-gray mt-0.5 line-clamp-2">
          {alert.message}
        </p>

        {alert.actionUrl && alert.actionLabel && (
          <Link
            to={alert.actionUrl}
            className={`
              inline-flex items-center gap-1 mt-2 text-sm font-medium
              ${config.actionClass} hover:underline
            `}
          >
            {alert.actionLabel}
            <ChevronRight className="w-4 h-4" />
          </Link>
        )}
      </div>

      {/* Dismiss Button */}
      <button
        onClick={onDismiss}
        className={`
          flex-shrink-0 p-1.5 rounded-lg transition-colors duration-200
          hover:bg-black/5
        `}
        aria-label="Dismiss alert"
      >
        <X className="w-4 h-4 text-warm-gray" />
      </button>
    </motion.div>
  );
};

// ============================================================================
// Alert Configuration
// ============================================================================

interface AlertConfig {
  icon: React.FC<{ className?: string }>;
  bgClass: string;
  borderClass: string;
  iconBgClass: string;
  iconClass: string;
  titleClass: string;
  actionClass: string;
}

function getAlertConfig(type: HealthAlert['type']): AlertConfig {
  const configs: Record<HealthAlert['type'], AlertConfig> = {
    urgent: {
      icon: AlertTriangle,
      bgClass: 'bg-red-50',
      borderClass: 'border-red-200',
      iconBgClass: 'bg-red-100',
      iconClass: 'text-red-600',
      titleClass: 'text-red-900',
      actionClass: 'text-red-600',
    },
    warning: {
      icon: AlertTriangle,
      bgClass: 'bg-terracotta/5',
      borderClass: 'border-terracotta/20',
      iconBgClass: 'bg-terracotta/10',
      iconClass: 'text-terracotta',
      titleClass: 'text-navy',
      actionClass: 'text-terracotta',
    },
    info: {
      icon: Info,
      bgClass: 'bg-blue-50',
      borderClass: 'border-blue-200',
      iconBgClass: 'bg-blue-100',
      iconClass: 'text-blue-600',
      titleClass: 'text-blue-900',
      actionClass: 'text-blue-600',
    },
    success: {
      icon: CheckCircle2,
      bgClass: 'bg-success/5',
      borderClass: 'border-success/20',
      iconBgClass: 'bg-success/10',
      iconClass: 'text-success',
      titleClass: 'text-navy',
      actionClass: 'text-success',
    },
  };

  return configs[type];
}

// ============================================================================
// Empty State
// ============================================================================

export const NoAlertsMessage: React.FC = () => (
  <div className="flex items-center gap-3 p-4 bg-cream rounded-xl">
    <div className="w-10 h-10 rounded-lg bg-sage/10 flex items-center justify-center">
      <Bell className="w-5 h-5 text-sage-dark" />
    </div>
    <div>
      <p className="font-medium text-navy">All caught up!</p>
      <p className="text-sm text-warm-gray">No alerts or reminders at this time.</p>
    </div>
  </div>
);

export default HealthAlerts;

